export const SUBSCRIPTION_STATE = Object.freeze({
  HUB_SETUP_REQUIRED: "HUB_SETUP_REQUIRED",
  ACTIVATION_REQUIRED: "ACTIVATION_REQUIRED",
  PAYMENT_PENDING: "PAYMENT_PENDING",
  TRIAL_ACTIVE: "TRIAL_ACTIVE",
  PAID_ACTIVE: "PAID_ACTIVE",
  SUBSCRIPTION_EXPIRED: "SUBSCRIPTION_EXPIRED",
  STAFF_ACCESS: "STAFF_ACCESS",
});

export function getPlanSnapshot(hubProfile) {
  const value = hubProfile?.Plan;
  if (value && typeof value === "object") {
    return {
      plan: String(value.plan || value.Plan || "").trim().toLowerCase(),
      expiry: value.expiry || value.ExpiryDate || hubProfile?.ExpiryDate || null,
      billingCycle: value.billingCycle ?? value.BillingCycle ?? hubProfile?.BillingCycle ?? null,
      catalogVersion: value.catalogVersion ?? value.CatalogVersion ?? hubProfile?.CatalogVersion ?? null,
      catalogChecksum: value.catalogChecksum ?? value.CatalogChecksum ?? hubProfile?.CatalogChecksum ?? null,
    };
  }
  return {
    plan: String(value || "").trim().toLowerCase(),
    expiry: hubProfile?.ExpiryDate || null,
    billingCycle: hubProfile?.BillingCycle || null,
    catalogVersion: hubProfile?.CatalogVersion || null,
    catalogChecksum: hubProfile?.CatalogChecksum || null,
  };
}

export function getHubPlanId(hubProfile) {
  return String(getPlanSnapshot(hubProfile).plan || "").trim().toLowerCase();
}

export function deriveHubSubscriptionState(hubProfile, now = new Date()) {
  if (!hubProfile) return SUBSCRIPTION_STATE.HUB_SETUP_REQUIRED;

  const explicitStatus = String(hubProfile.SubscriptionStatus || "").trim().toLowerCase();
  if (explicitStatus === "payment_pending") return SUBSCRIPTION_STATE.PAYMENT_PENDING;

  const plan = getPlanSnapshot(hubProfile);
  const planId = String(plan.plan || "").trim().toLowerCase();
  if (!planId || planId === "none") return SUBSCRIPTION_STATE.ACTIVATION_REQUIRED;

  const expiry = new Date(plan.expiry || "");
  if (Number.isNaN(expiry.getTime()) || expiry <= now) {
    return SUBSCRIPTION_STATE.SUBSCRIPTION_EXPIRED;
  }
  if (explicitStatus === "trial_active") return SUBSCRIPTION_STATE.TRIAL_ACTIVE;
  if (explicitStatus === "paid_active") return SUBSCRIPTION_STATE.PAID_ACTIVE;
  return SUBSCRIPTION_STATE.SUBSCRIPTION_EXPIRED;
}

export function isActiveSubscriptionState(state) {
  return state === SUBSCRIPTION_STATE.TRIAL_ACTIVE ||
    state === SUBSCRIPTION_STATE.PAID_ACTIVE;
}

export function resolveUserSubscriptionAccess(decodedToken, now = new Date()) {
  const ownerHubProfiles = (decodedToken?.MyHubAdmin || [])
    .map((entry) => entry?.HubProfile)
    .filter(Boolean);
  const staffHubProfiles = (decodedToken?.StaffAssignments || [])
    .map((entry) => entry?.HubProfile)
    .filter(Boolean);

  if (ownerHubProfiles.length === 0 && staffHubProfiles.length === 0) {
    return {
      state: SUBSCRIPTION_STATE.HUB_SETUP_REQUIRED,
      role: "owner",
      active: false,
      hubProfile: null,
    };
  }

  const ownerStates = ownerHubProfiles.map((hubProfile) => ({
    hubProfile,
    state: deriveHubSubscriptionState(hubProfile, now),
  }));
  const activeOwner = ownerStates.find(({ state }) => isActiveSubscriptionState(state));
  if (activeOwner) {
    return { ...activeOwner, role: "owner", active: true };
  }
  if (ownerStates.length > 0) {
    const pendingOwner = ownerStates.find(({ state }) => state === SUBSCRIPTION_STATE.PAYMENT_PENDING);
    const activationOwner = ownerStates.find(({ state }) => state === SUBSCRIPTION_STATE.ACTIVATION_REQUIRED);
    const selectedOwner = pendingOwner || activationOwner || ownerStates[0];
    return { ...selectedOwner, role: "owner", active: false };
  }

  const staffStates = staffHubProfiles.map((hubProfile) => ({
    hubProfile,
    ownerState: deriveHubSubscriptionState(hubProfile, now),
  }));
  const activeStaff = staffStates.find(({ ownerState }) => isActiveSubscriptionState(ownerState));
  return {
    state: SUBSCRIPTION_STATE.STAFF_ACCESS,
    role: "staff",
    active: Boolean(activeStaff),
    hubProfile: (activeStaff || staffStates[0])?.hubProfile || null,
    ownerState: (activeStaff || staffStates[0])?.ownerState || null,
  };
}
